import { addToast } from "@heroui/react";

import { UserJourneyData, JourneyQuestion } from "@/types/dataSchema";
import { logger } from "@/lib/secure-logger";
import { findFirstQuestion } from "@/lib/journey-utils";

import { Answers } from "../AnswerContext";
import { getNextQuestionId } from "../nextQuestionAlgorithm";

interface ParsedUrlParams {
  answers: Answers;
  startingQuestionId: string | null;
  hasErrors: boolean;
}

/**
 * Parses URL search parameters to extract prefilled answers
 * Format: ?qid1=aid1&qid2=aid2,aid3 (comma-separated for multi-select)
 *
 * Examples:
 * - Single select: ?entry_reason=security_issue
 * - Multi select: ?protection_type=waf,firewall
 * - Multiple questions: ?entry_reason=security_issue&protection_urgency=high
 *
 * Invalid parameters are ignored with a warning toast.
 * Non-question parameters (like utm_source) are silently ignored.
 */
export function parseUrlParams(
  searchParams: URLSearchParams,
  journeyData: UserJourneyData | null,
): ParsedUrlParams {
  const answers: Answers = {};
  let hasErrors = false;

  if (!journeyData?.questions) {
    return { answers: {}, startingQuestionId: null, hasErrors: false };
  }

  // Create maps for quick lookup
  const questionMap = new Map<string, JourneyQuestion>();
  const optionMap = new Map<string, Set<string>>();

  journeyData.questions.forEach((question) => {
    questionMap.set(question.id, question);
    const optionValues = new Set(question.options.map((opt) => opt.value));

    optionMap.set(question.id, optionValues);
  });

  // Process each URL parameter
  const entries = Array.from(searchParams.entries());

  logger.debug("URL parameters found:", entries);
  logger.debug("Available questions:", Array.from(questionMap.keys()));

  // Debug output
  logger.debug("🔍 URL Parameters Debug:", {
    entries,
    questionMapKeys: Array.from(questionMap.keys()),
    optionMapEntries: Array.from(optionMap.entries()).map(([k, v]) => [
      k,
      Array.from(v),
    ]),
  });

  for (const [questionId, answerValue] of entries) {
    logger.debug(`Processing parameter: ${questionId} = ${answerValue}`);

    // Skip any non-question parameters (like utm_source, etc.)
    if (!questionMap.has(questionId)) {
      logger.debug(`Question ID "${questionId}" not found in journey data`);
      continue;
    }

    // Skip empty values
    if (!answerValue || answerValue.trim() === "") {
      continue;
    }

    const question = questionMap.get(questionId)!;
    const validOptions = optionMap.get(questionId)!;

    // Handle multi-select by splitting on comma
    const answerValues = answerValue.split(",").map((v: string) => v.trim());

    // Validate all answer values exist as options
    const invalidAnswers = answerValues.filter(
      (val: string) => !validOptions.has(val),
    );

    if (invalidAnswers.length > 0) {
      hasErrors = true;
      addToast({
        title: "Invalid URL Parameter",
        description: `Question "${questionId}" does not have option(s): ${invalidAnswers.join(", ")}`,
        color: "warning",
      });
      continue;
    }

    // Store the answer in the correct format
    if (question.multiSelect) {
      answers[questionId] = answerValues;
    } else {
      if (answerValues.length > 1) {
        hasErrors = true;
        addToast({
          title: "Invalid URL Parameter",
          description: `Question "${questionId}" is single-select but multiple values provided: ${answerValues.join(", ")}`,
          color: "warning",
        });
        continue;
      }
      answers[questionId] = answerValues[0];
    }
  }

  // Calculate the starting question ID based on prefilled answers
  const startingQuestionId = calculateStartingQuestionId(answers, journeyData);

  const result = { answers, startingQuestionId, hasErrors };

  logger.debug("🎯 parseUrlParams returning:", result);

  return result;
}

/**
 * Calculates which question the user should start from based on prefilled answers
 */
function calculateStartingQuestionId(
  prefilledAnswers: Answers,
  journeyData: UserJourneyData,
): string | null {
  if (!journeyData.questions.length) {
    return null;
  }

  // If no prefilled answers, start from the beginning
  if (Object.keys(prefilledAnswers).length === 0) {
    const firstQuestion = findFirstQuestion(journeyData);

    return firstQuestion?.id || null;
  }

  // Simulate the journey progression with prefilled answers
  const firstQuestion = findFirstQuestion(journeyData);
  let currentQuestionId = firstQuestion?.id || null;

  if (!currentQuestionId) return null;
  const questionHistory: string[] = [];

  while (currentQuestionId && prefilledAnswers[currentQuestionId]) {
    questionHistory.push(currentQuestionId);

    const answerValue = prefilledAnswers[currentQuestionId];
    const selectedValues = Array.isArray(answerValue)
      ? answerValue
      : answerValue
        ? [answerValue]
        : [];

    // Use the existing algorithm to determine next question
    const nextId = getNextQuestionId({
      questions: journeyData.questions,
      questionHistory,
      currentQuestionId,
      skipped: false,
      selectedOptions: selectedValues,
      answers: prefilledAnswers,
    });

    if (!nextId) {
      // End of journey - show recommendations
      return "SHOW_RECOMMENDATIONS";
    }

    currentQuestionId = nextId;

    // Safety check to prevent infinite loops
    if (questionHistory.length > journeyData.questions.length) {
      break;
    }
  }

  return currentQuestionId;
}

/**
 * Builds the question history based on prefilled answers
 */
export function buildQuestionHistory(
  prefilledAnswers: Answers,
  journeyData: UserJourneyData,
): string[] {
  if (
    !journeyData.questions.length ||
    Object.keys(prefilledAnswers).length === 0
  ) {
    return [];
  }

  const history: string[] = [];
  const firstQuestion = findFirstQuestion(journeyData);
  let currentQuestionId = firstQuestion?.id;

  if (!currentQuestionId) return history;

  // Build the history by following the path of prefilled answers
  while (currentQuestionId && prefilledAnswers[currentQuestionId]) {
    history.push(currentQuestionId);

    const answerValue = prefilledAnswers[currentQuestionId];
    const selectedValues = Array.isArray(answerValue)
      ? answerValue
      : answerValue
        ? [answerValue]
        : [];

    // Use the same algorithm as in AnswerContext
    const nextId = getNextQuestionId({
      questions: journeyData.questions,
      questionHistory: history,
      currentQuestionId,
      skipped: false, // Prefilled answers are never skipped
      selectedOptions: selectedValues,
      answers: prefilledAnswers,
    });

    if (!nextId || nextId === "SHOW_RECOMMENDATIONS") {
      break;
    }

    currentQuestionId = nextId;

    // Safety check to prevent infinite loops
    if (history.length > journeyData.questions.length) {
      break;
    }
  }

  return history;
}

/**
 * Generates URL parameters to reach a specific question by building the required journey path
 */
export function generateUrlParamsToQuestion(
  targetQuestionId: string,
  journeyData: UserJourneyData,
): string {
  if (!journeyData.questions.length) {
    return "";
  }

  // Find all possible paths to the target question
  const targetQuestion = journeyData.questions.find(
    (q) => q.id === targetQuestionId,
  );

  if (!targetQuestion) {
    return "";
  }

  // Build a path by working backwards from the target question
  const requiredAnswers: Answers = {};
  const visitedQuestions = new Set<string>();

  // Start from the first question and find a path to the target
  function findPathToQuestion(
    currentId: string,
    targetId: string,
    answers: Answers,
  ): boolean {
    if (currentId === targetId) {
      return true;
    }

    if (visitedQuestions.has(currentId)) {
      return false; // Prevent infinite loops
    }

    visitedQuestions.add(currentId);

    const currentQuestion = journeyData.questions.find(
      (q) => q.id === currentId,
    );

    if (!currentQuestion) {
      return false;
    }

    // Try each option to see if it leads to the target
    for (const option of currentQuestion.options) {
      const testAnswers = { ...answers, [currentId]: option.value };

      const nextId = getNextQuestionId({
        questions: journeyData.questions,
        questionHistory: Object.keys(testAnswers),
        currentQuestionId: currentId,
        skipped: false,
        selectedOptions: [option.value],
        answers: testAnswers,
      });

      if (nextId && findPathToQuestion(nextId, targetId, testAnswers)) {
        requiredAnswers[currentId] = option.value;

        return true;
      }
    }

    return false;
  }

  // Start the search from the first question
  const firstQuestion = findFirstQuestion(journeyData);
  const firstQuestionId = firstQuestion?.id;

  if (!firstQuestionId) {
    logger.error("No first question found in journey data");

    return "";
  }

  if (findPathToQuestion(firstQuestionId, targetQuestionId, {})) {
    // Convert answers to URL parameters
    const params = new URLSearchParams();

    Object.entries(requiredAnswers).forEach(([questionId, answer]) => {
      if (Array.isArray(answer)) {
        params.set(questionId, answer.join(","));
      } else {
        params.set(questionId, answer);
      }
    });

    return params.toString();
  }

  return "";
}
